import React, {useEffect, useRef} from "react";
import L from "leaflet";
import {render} from "react-dom";

export const OrderDetailMarker = ({
    mapInstance,
    lat,
    lng,
    icon,
    text,
    color,
    size = 20,
    type = "default"
}) => {
    const markerRef = useRef(null);
    const iconContainerRef = useRef(null);
    const popupRef = useRef(null);

    useEffect(() => {
        if (!mapInstance || lat === undefined || lng === undefined) return;

        // Create custom icon based on type and props
        let customIcon;

        if (icon) {
            // Create a container div for the React icon
            const iconContainer = document.createElement("div");
            iconContainer.style.cssText = `
                width: ${size}px;
                height: ${size}px;
                background-color: ${color || "#ff4444"};
                border: 2px solid white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                position: relative;
            `;

            // Render React icon into the container
            render(icon, iconContainer);

            customIcon = L.divIcon({
                html: iconContainer,
                className: "order-detail-marker",
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2],
                popupAnchor: [0, -size / 2]
            });

            iconContainerRef.current = {container: iconContainer};
        } else {
            // Default marker with color - more like Google Maps style
            const iconHtml = `
                <div style="
                    width: ${size}px;
                    height: ${size}px;
                    background-color: ${color || "#ff4444"};
                    border: 2px solid white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                    font-size: ${size * 0.5}px;
                    color: white;
                    position: relative;
                ">
                    <div style="
                        width: ${size * 0.4}px;
                        height: ${size * 0.4}px;
                        background-color: white;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: ${size * 0.3}px;
                        color: ${color || "#ff4444"};
                    ">
                        📍
                    </div>
                </div>
            `;

            customIcon = L.divIcon({
                html: iconHtml,
                className: "order-detail-marker",
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2],
                popupAnchor: [0, -size / 2]
            });
        }

        if (markerRef.current) {
            const wasPopupOpen = markerRef.current.isPopupOpen();
            markerRef.current.setLatLng([lat, lng]);
            markerRef.current.setIcon(customIcon);

            if (wasPopupOpen && popupRef.current) {
                markerRef.current.openPopup();
            }
        } else {
            const marker = L.marker([lat, lng], {
                icon: customIcon
            });

            if (text) {
                const formattedText = text.replace(/\n/g, "<br>");
                const popupContent = `
                    <div style="
                        font-family: 'Roboto', Arial, sans-serif;
                        font-size: 14px;
                        line-height: 1.4;
                        color: #333;
                        padding: 8px 12px;
                        background: white;
                        border-radius: 4px;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                        max-width: 250px;
                        word-wrap: break-word;
                    ">
                        ${formattedText}
                    </div>
                `;
                const popup = marker.bindPopup(popupContent, {
                    className: "custom-popup",
                    closeButton: true,
                    autoClose: false,
                    closeOnClick: true // Close when clicking elsewhere on map
                });
                popupRef.current = popup;
            }

            // Add to map
            marker.addTo(mapInstance);
            markerRef.current = marker;
        }

        return () => {
            if (markerRef.current && !mapInstance.hasLayer(markerRef.current)) {
                mapInstance.removeLayer(markerRef.current);
                markerRef.current = null;
            }
            if (iconContainerRef.current) {
                // Clean up the rendered React component
                render(null, iconContainerRef.current.container);
                iconContainerRef.current = null;
            }
        };
    }, [mapInstance, lat, lng, icon, text, color, size, type]);

    return null;
};
